// Client Profile page where client can view and edit their company details, branding, contact information, 
// marketing preferences and accreditations. 
// This page also allows clients to upload their company logo and accreditation certificates, 
// which will be displayed on the inspection reports. The page includes form validation, 
// image compression for uploads, and a rich text editor for the company description.

import uploadIcon from "../../assets/img/uploadIcon.png";
import plusIcon from "../../assets/img/IconUpload.png";
import { hostName } from "../../config/config";
import {
  GET_CLIENT_COMPANY_DETAILS,
  GET_CLIENT_BRANDING_DETAILS,
  UPDATE_CLIENT_COMPANY_DETAILS,
  SAVE_CLIENT_COMPANY_DETAILS,
} from "../../api/apiPath";
import useGetClientId from "../../hooks/useGetClientId";
import { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams, generatePath } from "react-router-dom";
import useGetCity from "../../hooks/useGetCity";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import TextAlign from "@tiptap/extension-text-align";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableCell } from "@tiptap/extension-table-cell";
import { TableHeader } from "@tiptap/extension-table-header";
import DOMPurify from "dompurify";

import Header from "../common/Header";
import SidebarIcons from "../common/SidebarIcons";
import Sidebar from "../common/Sidebar";
import Toaster from "../common/Toaster";
import http from "../../api/server";
import imageCompression from "browser-image-compression";
import { MARKETING_CONSTANT } from "../common/constant";
import PhoneNumber from "../common/PhoneNumber";
import LoadingComponent from "../common/LoadingComponent";
import Footer from "../common/Footer";
import BottomBarIcons from "../common/BottomBarIcons";
import ConfirmationPopup from "../common/ConfirmationPopup";

const FORMDATA = {
  companyName: "",
  website: "",
  vatNumber: "",
  companyRegNumber: "",
  buildingName: "",
  streetAddress: "",
  city: "",
  cityId: "",
  postcode: "",
  countyRegion: "",
  country: "",
  emailAddress: "",
  contact1: "",
  countryCode1: "",
  dialCode1: "",
  contact2: "",
  countryCode2: "",
  dialCode2: "",
  companyDescription: "",
  noOfimages: null,
  pdfQuality: null,
  communalDoor: false,
};

const ClientBranding = () => {
  const { clientNameParam } = useParams();

  const [errors, setErrors] = useState(FORMDATA);
  const [error, setError] = useState(false);
  const [formData, setFormData] = useState({
    ...FORMDATA,
    noOfimages: 4,
    pdfQuality: 75,
  });

  const [marketingValues, setMarketingValues] = useState(MARKETING_CONSTANT);
  const [accFiles, setAccFiles] = useState([]);
  const [accFilePreviews, setAccFilePreviews] = useState([]);
  const [savedAccFiles, setSavedAccFiles] = useState([]);

  const phoneRef = useRef({});
  const [norecord, setNoRecord] = useState(false);
  const [showConfirmationPopup, setShowConfirmationPopup] = useState({
    show: false,
    headingText: "",
    infoText: "",
    cancelText: "",
    submittext: "",
  });
  const [refresh, setRefresh] = useState(true);

  const [portalName, setPortalName] = useState("");
  const [logoPreview, setLogoPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const inputRef = useRef(null);
  const accfileInputRef = useRef(null);
  const { userObj } = useSelector((state) => state.user);
  const [logoFile, setLogoFile] = useState(null);
  const [hideSidebar, setHideSidebar] = useState(false);
  const [summary, setSummary] = useState({});

  const clientObj = useGetClientId();
  const [clientLogo, setClientLogo] = useState("");
  const ImageProxyBaseUrl = hostName + "/api/Inspection/api/image?blobUrl=";

  const MAX_ACCREDITATIONS = 6;

  const isAccDisabled =
    savedAccFiles.length + accFilePreviews.length >= MAX_ACCREDITATIONS;

  const [toastData, setToastData] = useState({
    toastShow: false,
    toastType: "",
    toastString: "",
  });
  const closeButton = () => {
    setToastData({
      toastShow: false,
      toastData: "",
      toastType: "",
    });
  };

  //helper functions

  const handleDeleteLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    setClientLogo("");

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };
  const handleDeleteAccreditation = (index) => {
    const updatedSaved = savedAccFiles.filter((_, i) => i !== index);
    setSavedAccFiles(updatedSaved);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const file = e.dataTransfer.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setToastData({
        toastShow: true,
        toastType: "failure",
        toastString: "Only image files are allowed.",
      });
      return;
    }

    if (file.size > 4 * 1024 * 1024) {
      setToastData({
        toastShow: true,
        toastType: "failure",
        toastString: "File size exceeds 4MB limit.",
      });
      return;
    }

    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  //load city options for dropdown
  const [cityOptions, setCityOptions] = useState(null);

  const cityObj = useGetCity();

  useEffect(() => {
    setCityOptions(cityObj?.cityList);
  }, [cityObj.cityList, cityObj.loading]);

  //fallback if userobject is not loaded in time, to prevent infinite loading screen
  useEffect(() => {
    if (!userObj || !userObj?.token) {
      navigate(
        generatePath("/client/:clientNameParam", {
          clientNameParam,
        }),
        { replace: true, state: { from: "/ClientBranding" } },
      );
    }
  }, [userObj, navigate]);

  const handleClick = () => {
    fileInputRef.current.click();
  };
  const handleClickAcc = () => {
    accfileInputRef.current.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 4 * 1024 * 1024) {
        setToastData({
          toastShow: true,
          toastType: "failure",
          toastString:
            "File size exceeds 4MB limit. Please choose a smaller file.",
        });
        e.target.value = null; // Clear the input
        return;
      } else {
        setLogoFile(file);
        setLogoPreview(URL.createObjectURL(file));
      }
    }
  };
  const handleAccFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 4 * 1024 * 1024) {
        setToastData({
          toastShow: true,
          toastType: "failure",
          toastString:
            "File size exceeds 4MB limit. Please choose a smaller file.",
        });
        e.target.value = null; // Clear the input
        return;
      } else {
        const newFiles = [...accFiles, file];
        setAccFiles(newFiles);
        const newPrevs = [...accFilePreviews, URL.createObjectURL(file)];
        setAccFilePreviews(newPrevs);
      }
    }
  };

  /* =========================
     FETCH client profile DATA
     ========================= */
  useEffect(() => {
    if (clientObj.clientId) {
      const fetchBrandingDetails = async () => {
        try {
          const response = await http.get(
            GET_CLIENT_BRANDING_DETAILS + "?ClientId=" + clientObj.clientId,
          );
          if (response?.status === 200) {
            console.log("Branding details:", response.data);
            const data = response?.data?.clientDetails;
            setClientLogo(data?.clientLogoUrl || "");
            // setClientLogo("")
            setPortalName(data?.clientSubDomainName || "");
            setSummary({
              subdomain: data?.clientSubDomain || "",
              available: true,
            });
          }
        } catch (err) {
          console.error("Error fetching branding details:", err);
        }
      };
      fetchBrandingDetails();
    }
  }, [userObj, clientObj.clientId, clientObj.loading]);

  useEffect(() => {
    if (
      userObj &&
      !clientObj.loading &&
      clientObj.clientId &&
      refresh === true
    ) {
      const fetchCompanyDetails = async () => {
        try {
          const response = await http.get(
            GET_CLIENT_COMPANY_DETAILS + "?clientId=" + clientObj.clientId,
          );
          if (response?.status === 200) {
            console.log("Company details:", response);
            setNoRecord(false);
            const data = response?.data;
            setClientLogo(data?.clientLogo || "");
            const contact1Data = parsePhoneNumber(data?.contactNo1);
            const contact2Data = parsePhoneNumber(data?.contactNo2);

            setFormData({
              ...formData,
              companyName: data?.companyName || "",
              website: data?.website || "",
              vatNumber: data?.vatNumber || "",
              companyRegNumber: data?.companyRegistrationNumber || "",
              buildingName: data?.buildingName || "",
              streetAddress: data?.streetAddress || "",
              city: data?.city || "",
              cityId: String(data?.city || data?.cityId || ""),
              postcode: data?.postCode || "",
              // countyRegion: data?.region || "",
              countyRegion:
                formData.countyRegion === "" ? "" : data?.region || "",
              country: data?.country,
              emailAddress: data?.emailAddress || "",
              contact1: contact1Data?.number || "",
              countryCode1: contact1Data?.countryCode || "",
              dialCode1: contact1Data?.dialCode || "",
              contact2: contact2Data?.number || "",
              countryCode2: contact2Data?.countryCode || "",
              dialCode2: contact2Data?.dialCode || "",
              companyDescription: data?.companyDescription || "",
              clientCompanyInformationId: data?.clientCompanyInformationId || 0,
              noOfimages: data?.noMandatoryImage || 4,
              pdfQuality: data?.pdfQuality || 75,
              communalDoor: data?.includeCommunalDoorsInPdf || false,
            });
            setErrors(FORMDATA);
            //set marketing preferences
            if (data?.marketingPreferences != null) {
              const marketingPrefs = JSON.parse(data?.marketingPreferences);

              const titleToKey = {
                "Email Marketing": "Email",
                "SMS Notifications": "SMS",
                "Newsletter Subscription": "Newsletter",
                "Product Updates & Feature Releases": "Product",
                "Special Offers & Discounts": "Special",
              };

              setMarketingValues((prev) =>
                prev.map((item) => {
                  const key = titleToKey[item.title]; // <-- no 'as keyof' in .jsx
                  const checked = key ? Boolean(marketingPrefs[key]) : false;
                  return { ...item, isChecked: checked };
                }),
              );
            }
            if (data?.accreditations != null) {
              console.log(
                "Received accreditations from API:",
                data.accreditations.length,
                data.accreditations,
              );
              // Ensure accreditations is always an array
              const accreditationsArray = Array.isArray(data.accreditations)
                ? data.accreditations
                : [data.accreditations];
              setSavedAccFiles(accreditationsArray);
            } else {
              // If API returns null/undefined, clear saved files
              console.log(
                "No accreditations in API response, clearing saved files",
              );
              setSavedAccFiles([]);
            }
            setLogoFile(null);
            setLogoPreview(null);
            setAccFiles([]);
            setAccFilePreviews([]);
          }
        } catch (err) {
          console.error("Error fetching company details:", err.status);
          setNoRecord(true);
          if (err?.status === 404) {
            //no company details found
            setNoRecord(true);
          }
        } finally {
          setLoading(false);
          setRefresh(false);
        }
      };
      fetchCompanyDetails();
    }
  }, [userObj, clientObj.loading, refresh]);

  //update formdata on input change
  const handleFormChange = (e) => {
    if (e.target.name === "communalDoor") {
      const { name, checked } = e.target;
      setFormData({ ...formData, [name]: checked });
    } else {
      const { name, value } = e.target;
      setFormData({ ...formData, [name]: value });
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: value ? "" : prevErrors[name],
      }));
    }
  };

  // Initialize Tiptap editor
  const editor = useEditor({
    extensions: [
      StarterKit,
      Placeholder.configure({
        placeholder: "Tell us about your company, services...",
      }),
      Image,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          target: "_blank",
          rel: "noopener noreferrer",
        },
      }),
      // TextAlign.configure({
      //   types: ["heading", "paragraph"],
      // }),
      TextAlign.configure({
        types: ["heading", "paragraph", "listItem"],
      }),
      TextStyle,
      Color,
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content: formData.companyDescription || "",
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      // Sanitize HTML content to prevent XSS attacks
      const sanitizedValue = DOMPurify.sanitize(html, {
        ALLOWED_TAGS: [
          "p",
          "br",
          "strong",
          "em",
          "u",
          "s",
          "h1",
          "h2",
          "h3",
          "h4",
          "h5",
          "h6",
          "ul",
          "ol",
          "li",
          "a",
          "img",
          "blockquote",
          "code",
          "pre",
          "table",
          "thead",
          "tbody",
          "tr",
          "td",
          "th",
          "div",
          "span",
        ],
        ALLOWED_ATTR: [
          "href",
          "src",
          "alt",
          "title",
          "style",
          "class",
          "width",
          "height",
          "align",
        ],
        ALLOWED_URI_REGEXP:
          /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp):|[^a-z]|[a-z+.-]+(?:[^a-z+.-:]|$))/i,
      });
      setFormData({ ...formData, companyDescription: sanitizedValue });
      setErrors((prevErrors) => ({
        ...prevErrors,
        companyDescription: sanitizedValue ? "" : prevErrors.companyDescription,
      }));
    },
  });

  // Update editor content when formData changes (e.g., when loading from API)
  useEffect(() => {
    if (editor && formData.companyDescription !== editor.getHTML()) {
      // Don't sanitize when loading from API to preserve existing formatting
      editor.commands.setContent(formData.companyDescription || "", false);
    }
  }, [formData.companyDescription, editor]);

  // Helper function to parse formatted phone numbers from API
  const parsePhoneNumber = (formattedNumber) => {
    if (!formattedNumber) return { dialCode: "", number: "", countryCode: "" };

    // Extract dial code and number from format like "+91-8798598745"
    const match = formattedNumber.match(/^\+(\d+)-(.*)/);
    if (match) {
      const dialCode = match[1];
      const phoneNumber = match[2];

      return {
        dialCode: "",
        number: `+${dialCode}${phoneNumber}`, // Clean format for PhoneInput
        countryCode: "", // Let PhoneInput auto-detect country from dial code
      };
    }

    return { dialCode: "", number: formattedNumber, countryCode: "" };
  };

  const handleCheckbox = (e) => {
    const { id, checked } = e.target;
    const market = marketingValues;

    const newmarket = market.map((item) =>
      item.id.toString() == id ? { ...item, isChecked: checked } : item,
    );
    console.log(market, newmarket);

    setMarketingValues(newmarket);
  };

  const handlePhoneChange = (value, field) => {
    const rawNumber = value?.mobileNumber
      .replace(value?.mobileDialCode, "")
      .replace(/^\+/, "");
    const formattedNumber = `+${value?.mobileDialCode}-${rawNumber}` || "";
    setFormData({
      ...formData,
      ["contact" + field]: formattedNumber,
      ["dialCode" + field]: value.mobileDialCode,
      ["countryCode" + field]: value.mobileCountryCode,
    });
    console.log("formattedNumber", formattedNumber);
  };

  const emailRegex =
    /^(?:[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*|"[^"]+")@(?:(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,})$/;
  const ukPostcodeRegex = /^([A-Z]{1,2}\d[A-Z\d]? ?\d[A-Z]{2})$/i;

  /* =========================
     check validation errors and scroll to first error
     ========================= */
  const validate = () => {
    const newErrors = {};
    if (!emailRegex.test(formData.emailAddress) && formData.emailAddress)
      newErrors.emailAddress = "Please enter valid Email Address";
    if (!formData.emailAddress)
      newErrors.emailAddress = "Email Address is required";
    if (!formData.companyName)
      newErrors.companyName = "Company Name is required";
    // if (!formData.vatNumber) newErrors.vatNumber = "VAT Number is required";
    // if (!formData.companyRegNumber)
    //   newErrors.companyRegNumber = "Company Registration Number is required";
    if (!formData.buildingName)
      newErrors.buildingName = "Building Name is required";
    if (!formData.streetAddress)
      newErrors.streetAddress = "Street Address is required";
    if (!formData.cityId) newErrors.cityId = "City is required";
    if (!formData.country) newErrors.country = "Country is required";

    // Fix Contact 1 validation
    const contact1Clean = formData.contact1
      ?.replace(/^\+/, "")
      ?.replace(/-/g, "")
      ?.replace(formData?.dialCode1 || "", "")
      ?.trim();

    if (!formData.contact1 || !contact1Clean) {
      newErrors.contact1 = "Contact Number 1 is required";
      const input = phoneRef.current["contact1"];
      if (input) {
        input.parentNode.style.border = "2px solid #ef4444";
      }
    } else {
      // Clear any existing border styling when field is valid
      const input = phoneRef.current["contact1"];
      if (input) {
        input.parentNode.style.border = "";
      }
    }

    if (!formData.postcode) {
      newErrors.postcode = "Post Code is required";
    } else if (!ukPostcodeRegex.test(formData.postcode.trim())) {
      newErrors.postcode = "Invalid UK Post Code";
    }
    if (!formData.noOfimages || formData.noOfimages < 4) {
      newErrors.noOfimages = "At least 4 images required";
    }
    if (!formData.pdfQuality) {
      newErrors.pdfQuality = "PDF Quality is required";
    }
    if (
      !formData.companyDescription ||
      isEmptyEditorHtml(formData.companyDescription)
    )
      newErrors.companyDescription = "Please add company description";
    return newErrors;
  };

  // Treats "", "   ", "&nbsp;", "<p><br></p>", "<div></div>" etc. as empty
  const isEmptyEditorHtml = (html) => {
    if (html == null) return true; // null/undefined
    const s = String(html)
      .replace(/<!--.*?-->/gs, "") // remove comments
      .replace(/<style[\s\S]*?<\/style>/gi, "") // remove styles
      .replace(/<script[\s\S]*?<\/script>/gi, "") // remove scripts
      .replace(/&nbsp;?/gi, " ") // nbsp -> space
      .replace(/<br\s*\/?>/gi, " ") // <br> -> space
      .replace(
        /<\/?(p|div|span|strong|em|b|i|u|sup|sub|font|blockquote|h\d|ul|ol|li)\b[^>]*>/gi,
        " ",
      ) // strip common tags
      .trim();

    return s.length === 0;
  };

  /* =========================
     Save company DATA
     ========================= */

  const handleAddCompanyDetails = async () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setError(true);
      inputRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    } else {
      setError(false);
      setLoading(true);
      //add company details api call
      console.log("formData", formData);
      let compreedFile = null;
      if (logoFile) {
        const options = {
          maxSizeMB: 0.5, // Target size
          maxWidthOrHeight: 1024, // Optional resize
          useWebWorker: true,
          initialQuality: 0.7, // Start compression at 70%
        };
        const compressedFile = await imageCompression(logoFile, options);
        compreedFile = new File([compressedFile], logoFile.name, {
          type: logoFile.type,
        });
      }
      let compAccFiles = [];
      if (accFiles.length > 0) {
        compAccFiles = await createCompressedFiles();
      }

      setTimeout(() => {
        addData(compreedFile, compAccFiles);
      }, 1000);
    }
  };

  const addData = async (compreedFile, compAccFiles) => {
    const marketValues = {
      Email: marketingValues[0].isChecked,
      SMS: marketingValues[1].isChecked,
      Newsletter: marketingValues[2].isChecked,
      Product: marketingValues[3].isChecked,
      Special: marketingValues[4].isChecked,
    };

    const saveFormData = new FormData();
    saveFormData.append("ClientEmail", userObj?.userEmail);
    // saveFormData.append("ClientLogo", compreedFile);

    if (logoFile instanceof File && compreedFile) {
      saveFormData.append("ClientLogo", compreedFile);
    } else if (clientLogo) {
      saveFormData.append("ExistingClientLogo", clientLogo);
    }
    saveFormData.append("ClientSubDomainName", portalName);
    saveFormData.append("CompanyName", formData.companyName);
    saveFormData.append("Website", formData.website);
    saveFormData.append("VATNumber", formData.vatNumber);
    saveFormData.append("CompanyRegistrationNumber", formData.companyRegNumber);
    saveFormData.append("BuildingName", formData.buildingName);
    saveFormData.append("StreetAddress", formData.streetAddress);
    saveFormData.append("City", formData.cityId);
    saveFormData.append("PostCode", formData.postcode);
    saveFormData.append("Region", formData.countyRegion);
    saveFormData.append("Country", formData.country);
    saveFormData.append("NoMandatoryImage", formData.noOfimages);
    saveFormData.append("PDFQuality", formData.pdfQuality);
    saveFormData.append("IncludeCommunalDoorsInPdf", formData.communalDoor);
    saveFormData.append("EmailAddress", formData.emailAddress);
    saveFormData.append("ContactNo1", formData.contact1);
    saveFormData.append("ContactNo2", formData.contact2);
    saveFormData.append("CompanyDescription", formData.companyDescription);
    if (compAccFiles.length > 0) {
      compAccFiles.forEach((file) => {
        saveFormData.append("Accreditations", file);
      });
    }
    saveFormData.append("MarketingPreferences", JSON.stringify(marketValues));
    try {
      const response = await fetch(hostName + SAVE_CLIENT_COMPANY_DETAILS, {
        method: "POST",
        body: saveFormData,
        headers: {
          Authorization: "Bearer " + userObj?.token,
          ContentType: "multipart/form-data",
        },
      });
      if (response?.status === 200) {
        setToastData({
          toastShow: true,
          toastType: "success",
          toastString: "Company details updated successfully.",
        });
        setRefresh(true);
        setTimeout(() => {
          setRefresh(false);
        }, 200);
      } else {
        setToastData({
          toastShow: true,
          toastType: "failure",
          toastString: "Error adding details, please try again.",
        });
      }
      setLoading(false);
    } catch (err) {
      console.error("Error adding company details:", err);
      setToastData({
        toastShow: true,
        toastType: "failure",
        toastString: "Error adding details, please try again.",
      });
      setLoading(false);
    }
  };

  // const createCompressedFiles = async () => {
  //   let compAccFiles = [];
  //   accFiles.forEach(async (file) => {
  //     const options = {
  //       maxSizeMB: 0.5, // Target size
  //       maxWidthOrHeight: 1024, // Optional resize
  //       useWebWorker: true,
  //       initialQuality: 0.7, // Start compression at 70%
  //     };
  //     const compressedFile = await imageCompression(file, options);
  //     const compreedFile = new File([compressedFile], file.name, {
  //       type: file.type,
  //     });
  //     compAccFiles.push(compreedFile);
  //   });
  //   return compAccFiles;
  // };

  //compress large files before upload to improve performance and reduce bandwidth usage
  const createCompressedFiles = async () => {
    const options = {
      maxSizeMB: 0.5,
      maxWidthOrHeight: 1024,
      useWebWorker: true,
      initialQuality: 0.7,
    };

    const compressedFiles = await Promise.all(
      accFiles.map(async (file) => {
        const compressedFile = await imageCompression(file, options);
        return new File([compressedFile], file.name, {
          type: file.type,
        });
      }),
    );

    return compressedFiles;
  };

  //update company details
  const handleUpdateCompanyDetails = async () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      inputRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      setError(true);
    } else {
      //update company details api call
      setError(false);
      setLoading(true);
      console.log("formData", formData);
      let compreedFile = null;
      if (logoFile) {
        const options = {
          maxSizeMB: 0.5, // Target size
          maxWidthOrHeight: 1024, // Optional resize
          useWebWorker: true,
          initialQuality: 0.7, // Start compression at 70%
        };
        const compressedFile = await imageCompression(logoFile, options);
        compreedFile = new File([compressedFile], logoFile.name, {
          type: logoFile.type,
        });
      }
      let compAccFiles = [];
      if (accFiles.length > 0) {
        compAccFiles = await createCompressedFiles();
      }
      setTimeout(() => {
        saveData(compreedFile, compAccFiles);
      }, 500);
    }
  };
  //save function is separate to handle both add and update with same code, as the only difference is api endpoint and method
  const saveData = async (compreedFile, compAccFiles) => {
    const marketValues = {
      Email: marketingValues[0].isChecked,
      SMS: marketingValues[1].isChecked,
      Newsletter: marketingValues[2].isChecked,
      Product: marketingValues[3].isChecked,
      Special: marketingValues[4].isChecked,
    };
    const saveFormData = new FormData();
    saveFormData.append(
      "ClientCompanyInformationId",
      formData.clientCompanyInformationId,
    );
    saveFormData.append("ClientEmail", userObj?.userEmail);
    // saveFormData.append("ClientLogo", compreedFile);
    // Preserve existing client logo
    if (clientLogo) {
      saveFormData.append("ExistingClientLogo", clientLogo);
    } else {
      saveFormData.append("ExistingClientLogo", "");
    }

    if (logoFile instanceof File && compreedFile) {
      saveFormData.append("ClientLogo", compreedFile);
    }

    saveFormData.append("ClientSubDomainName", portalName);
    saveFormData.append("CompanyName", formData.companyName);
    saveFormData.append("Website", formData.website);
    saveFormData.append("VATNumber", formData.vatNumber);
    saveFormData.append("CompanyRegistrationNumber", formData.companyRegNumber);
    saveFormData.append("BuildingName", formData.buildingName);
    saveFormData.append("StreetAddress", formData.streetAddress);
    saveFormData.append("City", formData.cityId);
    saveFormData.append("PostCode", formData.postcode);
    saveFormData.append("Region", formData.countyRegion);
    saveFormData.append("Country", formData.country);
    saveFormData.append("NoMandatoryImage", formData.noOfimages);
    saveFormData.append("PDFQuality", formData.pdfQuality);
    saveFormData.append("IncludeCommunalDoorsInPdf", formData.communalDoor);
    saveFormData.append("EmailAddress", formData.emailAddress);
    saveFormData.append("ContactNo1", formData.contact1);
    saveFormData.append("ContactNo2", formData.contact2);
    saveFormData.append("CompanyDescription", formData.companyDescription);

    console.log(
      "Existing accreditations to preserve:",
      savedAccFiles.length,
      savedAccFiles,
    );
    savedAccFiles.forEach((url, index) => {
      console.log(`Appending existing accreditation ${index + 1}:`, url);
      saveFormData.append("ExistingAccreditations", url);
    });

    // if (savedAccFiles.length > 0) {
    //   savedAccFiles.forEach((url) => {
    //     saveFormData.append("ExistingAccreditations", url);
    //   });
    // } else {
    //   saveFormData.append("ExistingAccreditations", "");
    // }

    if (compAccFiles.length > 0) {
      console.log("Sending accreditations files:", compAccFiles.length);
      compAccFiles.forEach((file, index) => {
        console.log(
          `Appending accreditation file ${index + 1}:`,
          file.name,
          file.size,
        );
        saveFormData.append("Accreditations", file);
      });
    }

    saveFormData.append("MarketingPreferences", JSON.stringify(marketValues));

    try {
      const response = await fetch(hostName + UPDATE_CLIENT_COMPANY_DETAILS, {
        method: "PUT",
        body: saveFormData,
        headers: {
          Authorization: "Bearer " + userObj?.token,
          ContentType: "multipart/form-data",
        },
      });
      if (response?.status === 200) {
        setToastData({
          toastShow: true,
          toastType: "success",
          toastString: "Company details updated successfully.",
        });
        setRefresh(true);
        setTimeout(() => {
          setRefresh(false);
        }, 200);
      } else {
        setToastData({
          toastShow: true,
          toastType: "failure",
          toastString: "Error updating details, please try again.",
        });
        setLoading(false);
      }
    } catch (err) {
      console.error("Error updating company details:", err);
      setToastData({
        toastShow: true,
        toastType: "failure",
        toastString: "Error updating details, please try again.",
      });
      setLoading(false);
    }
  };

  //reset form to initial state
  const handleReset = () => {
    setFormData({
      ...formData,
      noOfimages: 4,
      pdfQuality: 75,
      companyName: "",
      website: "",
      vatNumber: "",
      companyRegNumber: "",
      buildingName: "",
      streetAddress: "",
      city: "",
      cityId: "",
      postcode: "",
      countyRegion: "",
      country: "",
      emailAddress: "",
      contact1: "",
      countryCode1: "",
      dialCode1: "",
      contact2: "",
      countryCode2: "",
      dialCode2: "",
      companyDescription: "",
      communalDoor: false,
    });
    setErrors(FORMDATA);
    setError(false);

    setLogoFile(null);
    setLogoPreview(null);

    setAccFiles([]);
    setAccFilePreviews([]);

    setSavedAccFiles([]);

    setMarketingValues(MARKETING_CONSTANT);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    if (accfileInputRef.current) {
      accfileInputRef.current.value = "";
    }

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <>
      <Header hideSidebar={hideSidebar} setHideSidebar={setHideSidebar} />

      <Toaster
        show={toastData.toastShow}
        toastType={toastData.toastType}
        title={toastData.toastString}
        theme={"dark"}
        position="top-center"
        closeButton={closeButton}
      />
      <ConfirmationPopup
        show={showConfirmationPopup.show}
        headingText={showConfirmationPopup.headingText}
        infoText={showConfirmationPopup.infoText}
        cancelText={showConfirmationPopup.cancelText}
        submitText={showConfirmationPopup.submittext}
        onCancel={() => {
          setShowConfirmationPopup({
            ...showConfirmationPopup,
            show: false,
            headingText: "",
            infoText: "",
            cancelText: "",
            submittext: "",
          });
        }}
        onSubmit={() => {
          if (showConfirmationPopup.onConfirmType === "logo") {
            handleDeleteLogo();
          }

          if (showConfirmationPopup.onConfirmType === "accreditation") {
            handleDeleteAccreditation(showConfirmationPopup.deleteIndex);
          }

          // fallback → save/update
          if (!showConfirmationPopup.onConfirmType) {
            norecord ? handleAddCompanyDetails() : handleUpdateCompanyDetails();
          }

          setShowConfirmationPopup({
            show: false,
            headingText: "",
            infoText: "",
            cancelText: "",
            submittext: "",
          });
        }}
      />
      <LoadingComponent loading={loading} />
      {userObj && (
        <div className="dashboard_body_main">
          {hideSidebar ? (
            <Sidebar
              setHideSidebar={setHideSidebar}
              hideSidebar={hideSidebar}
            />
          ) : (
            <SidebarIcons
              setHideSidebar={setHideSidebar}
              hideSidebar={hideSidebar}
            />
          )}
          <div className="d-flex dashboard_body flex-column py-1 w-100 mt-2">
            <div className="dashboard_header">
              <h2 className="mb-0">Client Profile</h2>
              <div className="d-flex flex-row justify-content-between">
                <p className="header-description">
                  Manage and track all inspections
                </p>
                <div className="client_profile_subdomain">
                  {summary?.subdomain}
                </div>
              </div>
            </div>
            <div className="resident-card d-flex flex-column gap-2 mt-3 text-start">
              <h3 className="resident-card-header">Upload Logo</h3>
              <div className="upload-section">
                {/* <div className="upload-box-client" onClick={handleClick}> */}
                <div
                  className="upload-box-client"
                  onClick={handleClick}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                >
                  <div className="upload-content">
                    {(logoPreview || clientLogo) && (
                      <span
                        className="delete-icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowConfirmationPopup({
                            show: true,
                            headingText: "Delete Logo",
                            infoText:
                              "Are you sure you want to delete the logo?",
                            cancelText: "Cancel",
                            submittext: "Delete",
                            onConfirmType: "logo",
                          });
                        }}
                      >
                        ✕
                      </span>
                    )}

                    {logoPreview ? (
                      <>
                        <img
                          src={logoPreview}
                          alt="Uploaded Logo"
                          className="upload-box-image"
                        />
                      </>
                    ) : (
                      <img
                        // style={{ height: "100px", width: "100px" }}
                        className={
                          clientLogo ? "upload-box-image" : "upload-box-icon"
                        }
                        src={
                          clientLogo
                            ? ImageProxyBaseUrl + encodeURIComponent(clientLogo)
                            : uploadIcon
                        }
                        alt="upload"
                      />
                    )}
                  </div>
                </div>
                <div className="upload-info ms-3">
                  <p className="upload-text">
                    Drag & drop your file here or{" "}
                    <span className="browse-link" onClick={handleClick}>
                      click to browse
                    </span>
                  </p>
                  <small className="upload-hint">PNG, JPG up to 4MB</small>
                  <p className="remark-text">
                    *Logo will be used in all reports and communication. Logo
                    can be changed in the settings.
                  </p>
                </div>
                <input
                  type="file"
                  accept="image/png, image/jpeg"
                  style={{ display: "none" }}
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  max={4}
                />
              </div>
              <div>
                <p className="remark-text mb-0">
                  *Recommended Specifications: 200x200px (PNG or JPG). Please
                  ensure your logo matches these dimensions to avoid visual
                  distortion and maintain image clarity.
                </p>
              </div>
            </div>
            <div className="resident-card d-flex flex-column gap-2 mt-3 text-start">
              <h3 className="resident-card-header">Company Information</h3>
              <div className="card-body-section">
                {/* <div className="form-group" ref={inputRef}>
                  <label htmlFor="companyName">
                    Company Name <span className="label-star">*</span>
                  </label>
                  <input
                    className={errors.companyName ? "input-error" : ""}
                    name="companyName"
                    id="companyName"
                    placeholder="Enter company name"
                    aria-label="Floating label select example"
                    value={formData.companyName}
                    // disabled={true}
                    onChange={(e) => {
                      handleFormChange(e);
                    }}
                  ></input>
                  {errors.companyName && (
                    <span className="formError">{errors.companyName}</span>
                  )}
                </div> */}
                <div className="form-group" ref={inputRef}>
                  <label htmlFor="companyName">
                    Company Name <span className="label-star">*</span>
                  </label>
                  <input
                    type="text"
                    className={errors.companyName ? "input-error" : ""}
                    name="companyName"
                    id="companyName"
                    placeholder="Enter company name"
                    value={formData.companyName}
                    onChange={handleFormChange}
                  />
                  {errors.companyName && (
                    <span className="formError">{errors.companyName}</span>
                  )}
                </div>
                <div className="form-group">
                  <label htmlFor="website">Website</label>
                  <input
                    className={errors.website ? "input-error" : ""}
                    name="website"
                    id="website"
                    placeholder="Enter website"
                    aria-label="Floating label select example"
                    value={formData.website}
                    // disabled={true}
                    onChange={(e) => {
                      handleFormChange(e);
                    }}
                  ></input>
                  {errors.website && (
                    <span className="formError">{errors.website}</span>
                  )}
                </div>
                <div className="form-group">
                  <label htmlFor="vatNumber">
                    VAT Number
                    {/* <span className="label-star">*</span> */}
                  </label>
                  <input
                    className={errors.vatNumber ? "input-error" : ""}
                    name="vatNumber"
                    id="vatNumber"
                    placeholder="Enter VAT number"
                    aria-label="Floating label select example"
                    value={formData.vatNumber}
                    // disabled={true}
                    onChange={(e) => {
                      handleFormChange(e);
                    }}
                  ></input>
                  {/* {errors.vatNumber && (
                    <span className="formError">{errors.vatNumber}</span>
                  )} */}
                </div>
                <div className="form-group">
                  <label htmlFor="companyRegNumber">
                    Company Registration Number{" "}
                    {/* <span className="label-star">*</span> */}
                  </label>
                  <input
                    className={errors.companyRegNumber ? "input-error" : ""}
                    name="companyRegNumber"
                    id="companyRegNumber"
                    placeholder="Enter company registration number"
                    aria-label="Floating label select example"
                    value={formData.companyRegNumber}
                    // disabled={true}
                    onChange={(e) => {
                      handleFormChange(e);
                    }}
                  ></input>
                  {/* {errors.companyRegNumber && (
                    <span className="formError">{errors.companyRegNumber}</span>
                  )} */}
                </div>
              </div>
            </div>
            <div className="resident-card d-flex flex-column gap-2 mt-3 text-start">
              <h3 className="resident-card-header">Company Address</h3>
              <div className="form-group">
                <label htmlFor="buildingName">
                  Building Name/Number <span className="label-star">*</span>
                </label>
                <input
                  className={errors.buildingName ? "input-error" : ""}
                  name="buildingName"
                  id="buildingName"
                  placeholder="Enter building name/number"
                  aria-label="Floating label select example"
                  value={formData.buildingName}
                  // disabled={true}
                  onChange={(e) => {
                    handleFormChange(e);
                  }}
                ></input>
                {errors.buildingName && (
                  <span className="formError">{errors.buildingName}</span>
                )}
              </div>
              <div className="form-group mt-1">
                <label htmlFor="streetAddress">
                  Street Address <span className="label-star">*</span>
                </label>
                <input
                  className={errors.streetAddress ? "input-error" : ""}
                  name="streetAddress"
                  id="streetAddress"
                  placeholder="Enter street address"
                  aria-label="Floating label select example"
                  value={formData.streetAddress}
                  // disabled={true}
                  onChange={(e) => {
                    handleFormChange(e);
                  }}
                ></input>
                {errors.streetAddress && (
                  <span className="formError">{errors.streetAddress}</span>
                )}
              </div>
              <div className="card-body-section mt-1">
                <div className="form-group">
                  <label htmlFor="city">
                    City <span className="label-star">*</span>
                  </label>
                  <select
                    className={errors.cityId ? "input-error" : ""}
                    name="cityId"
                    id="city"
                    placeholder="Enter city"
                    aria-label="Floating label select example"
                    value={String(formData.cityId || "")}
                    // disabled={true}
                    onChange={(e) => {
                      handleFormChange(e);
                    }}
                  >
                    <option value="">Select City</option>
                    {cityOptions?.map((data) => (
                      <option key={data?.cityId} value={String(data?.cityId)}>
                        {data.cityName}
                      </option>
                    ))}
                  </select>
                  {errors.cityId && (
                    <span className="formError">{errors.cityId}</span>
                  )}
                </div>
                <div className="form-group">
                  <label htmlFor="postcode">
                    Post Code <span className="label-star">*</span>
                  </label>
                  <input
                    className={errors.postcode ? "input-error" : ""}
                    name="postcode"
                    id="postcode"
                    placeholder="Enter post code"
                    aria-label="Floating label select example"
                    value={formData.postcode}
                    // disabled={true}
                    onChange={(e) => {
                      handleFormChange(e);
                    }}
                  ></input>
                  {errors.postcode && (
                    <span className="formError">{errors.postcode}</span>
                  )}
                </div>
                <div className="form-group">
                  <label htmlFor="countyRegion">County/Region</label>
                  <input
                    className={errors.countyRegion ? "input-error" : ""}
                    name="countyRegion"
                    id="countyRegion"
                    placeholder="Enter county/region"
                    aria-label="Floating label select example"
                    value={formData.countyRegion || ""}
                    // disabled={true}
                    onChange={(e) => {
                      handleFormChange(e);
                    }}
                  ></input>
                  {errors.countyRegion && (
                    <span className="formError">{errors.countyRegion}</span>
                  )}
                </div>
                <div className="form-group">
                  <label htmlFor="country">
                    Country <span className="label-star">*</span>
                  </label>
                  <select
                    className={errors.country ? "input-error" : ""}
                    name="country"
                    id="country"
                    placeholder="Enter country"
                    aria-label="Floating label select example"
                    value={formData.country}
                    // disabled={true}
                    onChange={(e) => {
                      handleFormChange(e);
                    }}
                  >
                    <option value="">Select Country</option>
                    <option value="UK">UK</option>
                  </select>
                  {errors.country && (
                    <span className="formError">{errors.country}</span>
                  )}
                </div>
              </div>
            </div>
            <div className="resident-card d-flex flex-column gap-2 mt-3 text-start">
              <h3 className="resident-card-header">Contact Information</h3>
              <div className="card-body-three-section">
                <div className="form-group">
                  <label htmlFor="emailAddress">
                    Email Address <span className="label-star">*</span>
                  </label>
                  <input
                    className={errors.emailAddress ? "input-error" : ""}
                    name="emailAddress"
                    id="emailAddress"
                    placeholder="Enter email address"
                    aria-label="Floating label select example"
                    value={formData.emailAddress}
                    // disabled={true}
                    onChange={(e) => {
                      handleFormChange(e);
                    }}
                  ></input>
                  {errors.emailAddress && (
                    <span className="formError">{errors.emailAddress}</span>
                  )}
                </div>
                <div className="form-group">
                  <label htmlFor="contact1">
                    Contact Number 1 <span className="label-star">*</span>
                  </label>
                  <PhoneNumber
                    setPhoneNumber={(e) => {
                      handlePhoneChange(e, "1");
                    }}
                    phoneNumber={{
                      mobileNumber: formData.contact1,
                      mobileCountryCode: formData.countryCode1,
                      mobileDialCode: formData.dialCode1,
                    }}
                    inputRef={(el) => (phoneRef.current["contact1"] = el)}
                  />
                  {errors.contact1 && (
                    <span className="formError">{errors.contact1}</span>
                  )}
                </div>
                <div className="form-group">
                  <label htmlFor="contact2">Contact Number 2</label>
                  <PhoneNumber
                    setPhoneNumber={(e) => {
                      handlePhoneChange(e, "2");
                    }}
                    phoneNumber={{
                      mobileNumber: formData.contact2,
                      mobileCountryCode: formData.countryCode2,
                      mobileDialCode: formData.dialCode2,
                    }}
                    inputRef={(el) => (phoneRef.current["contact2"] = el)}
                  />
                  {errors.contact2 && (
                    <span className="formError">{errors.contact2}</span>
                  )}
                </div>
              </div>
            </div>
            <div className="resident-card d-flex flex-column gap-2 mt-3 text-start">
              <h3 className="resident-card-header">Additional Details</h3>
              <div className="form-group">
                <label htmlFor="companyDescription">
                  Company Description<span className="label-star">*</span>
                </label>
                <div
                  className={
                    errors.companyDescription ? "rich-text-editor-error" : ""
                  }
                >
                  {editor && (
                    <>
                      <div className="tiptap-toolbar">
                        <button
                          type="button"
                          onClick={() =>
                            editor.chain().focus().toggleBold().run()
                          }
                          className={editor.isActive("bold") ? "is-active" : ""}
                          title="Bold"
                        >
                          <strong>B</strong>
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            editor.chain().focus().toggleItalic().run()
                          }
                          className={
                            editor.isActive("italic") ? "is-active" : ""
                          }
                          title="Italic"
                        >
                          <em>I</em>
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            editor.chain().focus().toggleUnderline().run()
                          }
                          className={
                            editor.isActive("underline") ? "is-active" : ""
                          }
                          title="Underline"
                        >
                          <u>U</u>
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            editor.chain().focus().toggleStrike().run()
                          }
                          className={
                            editor.isActive("strike") ? "is-active" : ""
                          }
                          title="Strike"
                        >
                          <s>S</s>
                        </button>
                        <div className="toolbar-divider"></div>
                        <button
                          type="button"
                          onClick={() =>
                            editor
                              .chain()
                              .focus()
                              .toggleHeading({ level: 1 })
                              .run()
                          }
                          className={
                            editor.isActive("heading", { level: 1 })
                              ? "is-active"
                              : ""
                          }
                          title="Heading 1"
                        >
                          H1
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            editor
                              .chain()
                              .focus()
                              .toggleHeading({ level: 2 })
                              .run()
                          }
                          className={
                            editor.isActive("heading", { level: 2 })
                              ? "is-active"
                              : ""
                          }
                          title="Heading 2"
                        >
                          H2
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            editor
                              .chain()
                              .focus()
                              .toggleHeading({ level: 3 })
                              .run()
                          }
                          className={
                            editor.isActive("heading", { level: 3 })
                              ? "is-active"
                              : ""
                          }
                          title="Heading 3"
                        >
                          H3
                        </button>
                        <div className="toolbar-divider"></div>
                        <button
                          type="button"
                          onClick={() =>
                            editor.chain().focus().toggleBulletList().run()
                          }
                          className={
                            editor.isActive("bulletList") ? "is-active" : ""
                          }
                          title="Bullet List"
                        >
                          •
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            editor.chain().focus().toggleOrderedList().run()
                          }
                          className={
                            editor.isActive("orderedList") ? "is-active" : ""
                          }
                          title="Numbered List"
                        >
                          1.
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            editor.chain().focus().toggleBlockquote().run()
                          }
                          className={
                            editor.isActive("blockquote") ? "is-active" : ""
                          }
                          title="Quote"
                        >
                          &quot;
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            editor.chain().focus().toggleCodeBlock().run()
                          }
                          className={
                            editor.isActive("codeBlock") ? "is-active" : ""
                          }
                          title="Code Block"
                        >
                          {"</>"}
                        </button>
                        <div className="toolbar-divider"></div>
                        <button
                          type="button"
                          onClick={() =>
                            editor.chain().focus().setTextAlign("left").run()
                          }
                          className={
                            editor.isActive({ textAlign: "left" })
                              ? "is-active"
                              : ""
                          }
                          title="Align Left"
                        >
                          ⬅
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            editor.chain().focus().setTextAlign("center").run()
                          }
                          className={
                            editor.isActive({ textAlign: "center" })
                              ? "is-active"
                              : ""
                          }
                          title="Align Center"
                        >
                          ⬌
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            editor.chain().focus().setTextAlign("right").run()
                          }
                          className={
                            editor.isActive({ textAlign: "right" })
                              ? "is-active"
                              : ""
                          }
                          title="Align Right"
                        >
                          ➡
                        </button>
                        <div className="toolbar-divider"></div>
                        <input
                          type="color"
                          onInput={(e) =>
                            editor
                              .chain()
                              .focus()
                              .setColor(e.target.value)
                              .run()
                          }
                          title="Text Color"
                          style={{
                            width: "30px",
                            height: "30px",
                            cursor: "pointer",
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const url = window.prompt("Enter URL:");
                            if (url) {
                              editor
                                .chain()
                                .focus()
                                .setLink({ href: url })
                                .run();
                            }
                          }}
                          className={editor.isActive("link") ? "is-active" : ""}
                          title="Insert Link"
                        >
                          🔗
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            editor
                              .chain()
                              .focus()
                              .insertTable({
                                rows: 3,
                                cols: 3,
                                withHeaderRow: true,
                              })
                              .run()
                          }
                          title="Insert Table"
                        >
                          ⧉
                        </button>
                        <div className="toolbar-divider"></div>
                        <button
                          type="button"
                          onClick={() =>
                            editor
                              .chain()
                              .focus()
                              .clearNodes()
                              .unsetAllMarks()
                              .run()
                          }
                          title="Clear Formatting"
                        >
                          ✕
                        </button>
                      </div>
                      <EditorContent editor={editor} />
                    </>
                  )}
                </div>
                {errors.companyDescription && (
                  <span className="formError">{errors.companyDescription}</span>
                )}
              </div>
            </div>
            <div className="resident-card d-flex flex-column gap-2 mt-3 text-start">
              <h3 className="resident-card-header">Accreditations</h3>
              <div className="d-flex flex-row gap-2">
                {savedAccFiles?.length > 0 &&
                  savedAccFiles.map((url, index) => {
                    return (
                      <div key={index} className="uploaded-image-client">
                        <span
                          className="delete-icon"
                          onClick={() =>
                            setShowConfirmationPopup({
                              show: true,
                              headingText: "Delete Accreditation",
                              infoText:
                                "Are you sure you want to delete this accreditation?",
                              cancelText: "Cancel",
                              submittext: "Delete",
                              onConfirmType: "accreditation",
                              deleteIndex: index,
                            })
                          }
                        >
                          ✕
                        </span>

                        <img
                          src={ImageProxyBaseUrl + encodeURIComponent(url)}
                          alt="Uploaded Logo"
                          style={{ height: "9rem", width: "9rem" }}
                        />
                      </div>
                    );
                  })}
                {/* {accFilePreviews?.length > 0 &&
                  accFilePreviews.map((url, index) => {
                    return (
                      <div key={index} className="uploaded-image-client">
                        <img
                          src={url}
                          alt="Uploaded Logo"
                          style={{ height: "9rem", width: "9rem" }}
                        />
                      </div>
                    );
                  })} */}
                {accFilePreviews?.length > 0 &&
                  accFilePreviews.map((url, index) => {
                    return (
                      <div key={index} className="uploaded-image-client">
                        <span
                          className="delete-icon"
                          onClick={() => {
                            const updatedFiles = accFiles.filter(
                              (_, i) => i !== index,
                            );
                            const updatedPreviews = accFilePreviews.filter(
                              (_, i) => i !== index,
                            );

                            setAccFiles(updatedFiles);
                            setAccFilePreviews(updatedPreviews);
                            setShowConfirmationPopup({
                              show: true,
                              headingText: "Delete Accreditation",
                              infoText:
                                "Are you sure you want to delete this accreditation?",
                              cancelText: "Cancel",
                              submittext: "Delete",
                              onConfirmType: "new-accreditation",
                              deleteIndex: index,
                            });
                          }}
                        >
                          ✕
                        </span>

                        <img
                          src={url}
                          alt="Uploaded Accreditation"
                          style={{ height: "9rem", width: "9rem" }}
                        />
                      </div>
                    );
                  })}

                <div
                  className={`upload-box-client ${isAccDisabled ? "disabled" : ""}`}
                  onClick={() => {
                    if (!isAccDisabled) {
                      handleClickAcc();
                    }
                  }}
                >
                  <div
                    className="upload-content flex-column"
                    // onClick={handleClickAcc}
                  >
                    <img
                      style={{ height: "1.5rem", width: "1.5rem" }}
                      src={plusIcon}
                      alt="upload"
                    />
                    <span style={{ fontSize: "0.75rem", fontWeight: "500" }}>
                      Accreditations
                    </span>
                    <input
                      type="file"
                      accept="image/png, image/jpeg"
                      style={{ display: "none" }}
                      ref={accfileInputRef}
                      onChange={handleAccFileChange}
                      max={4}
                      disabled={isAccDisabled}
                    />
                  </div>
                </div>
              </div>
              <div>
                <p className="remark-text mb-0">
                  *Recommended Specifications: 200x200px (PNG or JPG). Please
                  ensure your logo matches these dimensions to avoid visual
                  distortion and maintain image clarity.
                </p>
              </div>
            </div>
            <div className="resident-card d-flex flex-column gap-2 mt-3 text-start">
              <h3 className="resident-card-header">PDF Settings</h3>
              <div className="card-body-section mt-1" style={{ rowGap: "0" }}>
                <div className="form-group">
                  <label htmlFor="countyRegion">
                    Number Of Mandatory Images
                    <span className="label-star">*</span>
                  </label>
                  <input
                    type="number"
                    pattern="[0-9]*"
                    inputMode="integer"
                    onKeyDown={(e) => {
                      if (e.key === "-" || e.key === "e" || e.key === ".") {
                        e.preventDefault(); // Prevent invalid characters
                      }
                    }}
                    min={4}
                    className={errors.noOfimages ? "input-error" : ""}
                    name="noOfimages"
                    id="noOfimages"
                    placeholder="Enter number of mandatory images required"
                    aria-label="Floating label select example"
                    value={formData.noOfimages || ""}
                    // disabled={true}
                    onChange={(e) => {
                      handleFormChange(e);
                    }}
                  ></input>
                  {errors.noOfimages && (
                    <span className="formError">{errors.noOfimages}</span>
                  )}
                </div>
                <div className="form-group">
                  <label htmlFor="pdf">
                    PDF Quality <span className="label-star">*</span>
                    <i
                      className="bi bi-info-circle"
                      style={{
                        fontSize: "0.9rem",
                        marginTop: "-7px",
                        marginLeft: "5px",
                      }}
                      title="Please note that at 100% quality the PDF will contain images at the highest quality and resolution. 
                    This gives the best clarity but results in a larger file size."
                    ></i>
                  </label>
                  <input
                    type="range"
                    min="75"
                    max="100"
                    step="1"
                    className={errors.pdfQuality ? "input-error" : ""}
                    style={{ padding: "0px", accentColor: "#212121" }}
                    name="pdfQuality"
                    id="pdfQuality"
                    placeholder="Enter PDF Quality"
                    aria-label="Floating label select example"
                    value={formData.pdfQuality}
                    // disabled={true}
                    onChange={(e) => {
                      handleFormChange(e);
                    }}
                  />
                  <p>{formData.pdfQuality}%</p>
                  {errors.country && (
                    <span className="formError">{errors.country}</span>
                  )}
                </div>
                <div className="d-flex align-items-start gap-2">
                  <input
                    className="custom-checkbox mt-1 form-check-input-checkbox"
                    type="checkbox"
                    id="communalDoor"
                    name="communalDoor"
                    // disabled={!isAppprovalPending}
                    checked={formData.communalDoor}
                    onChange={(e) => handleFormChange(e)}
                  />
                  <div>
                    <p className="marketing-header mb-0">
                      Include Consolidated action item Summary Icon
                    </p>
                    <p
                      className="marketing-description mb-0"
                      style={{ fontSize: "0.7rem" }}
                    >
                      (Disabling this will remove the consolidated action item,
                      though details of each door will be available in PDF)
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="resident-card d-flex flex-column gap-2 mt-3 text-start">
              <div>
                <h3 className="resident-card-header mb-1">
                  Marketing and Promotional Offers
                </h3>
                <p className="resident-card-subheader">
                  Choose how you&apos;d like to receive updates and promotional
                  offers from us.
                </p>
              </div>
              <div className="d-flex flex-column gap-3">
                {marketingValues?.length > 0 &&
                  marketingValues?.map((item) => {
                    return (
                      <div className="marketing-box" key={item.id}>
                        <input
                          className="custom-checkbox mt-1"
                          type="checkbox"
                          id={item.id}
                          name={item.id}
                          // disabled={!isAppprovalPending}
                          checked={item?.isChecked}
                          onChange={(e) => handleCheckbox(e)}
                        />
                        <div>
                          <p className="marketing-header mb-0">{item.title}</p>
                          <p className="marketing-description mb-0">
                            {item.description}
                          </p>
                        </div>
                      </div>
                    );
                  })}
              </div>
              <div className="privacy-notice-box mt-2">
                <p
                  className="mb-0"
                  style={{
                    fontWeight: "600",
                    fontSize: "0.7rem",
                    color: " #171717",
                  }}
                >
                  Privacy Notice:&nbsp;{" "}
                </p>
                <p
                  className="mb-0"
                  style={{
                    fontWeight: "300",
                    fontSize: "0.7rem",
                    color: " #737373",
                  }}
                >
                  We respect your privacy. You can unsubscribe from any of these
                  communications at any time. Your data will be handled in
                  accordance with our Privacy Policy and GDPR regulations.
                </p>
              </div>
            </div>
            {error && (
              <div
                className="mt-2 formError"
                style={{ fontSize: "0.8rem", textAlign: "end" }}
              >
                Please fill in all required fields.
              </div>
            )}
            <div className="button-container">
              {norecord && (
                <button
                  type="submit"
                  className="btn-primary col-sm-5 col-md-2"
                  onClick={() =>
                    setShowConfirmationPopup({
                      ...showConfirmationPopup,
                      show: true,
                      headingText: "Add Company Details",
                      infoText:
                        "Please confirm to add the entered company details.",
                      cancelText: "Cancel",
                      submittext: "Save",
                    })
                  }
                >
                  Submit
                </button>
              )}
              {!norecord && (
                <button
                  type="submit"
                  className="btn-primary col-sm-5 col-md-2"
                  onClick={() =>
                    setShowConfirmationPopup({
                      ...showConfirmationPopup,
                      show: true,
                      headingText: "Update Company Details",
                      infoText:
                        "Please confirm to update the entered company details.",
                      cancelText: "Cancel",
                      submittext: "Update",
                    })
                  }
                >
                  Update
                </button>
              )}
              <button
                type="reset"
                className="btn-secondary col-sm-5 col-md-2"
                onClick={handleReset}
              >
                Reset
              </button>
            </div>
          </div>
          <Footer />
          <div className="bottom-bar-spacing"></div>
          <BottomBarIcons />
        </div>
      )}
    </>
  );
};

export default ClientBranding;
